<template>
  <div class="relative">
    <div class="flex">
      <h2 v-if="header" class="flex-grow break-all font-barlow text-[24px] font-black leading-none">
        {{ header }}

        <span v-if="subheader && showSubheader" class="block text-md font-normal text-white/60">
          {{ subheader }}
        </span>
      </h2>

      <!-- todo: no data message  -->
      <directory-switcher
        class="mb-4"
        v-model:view="view"
      />
    </div>

    <component
      v-if="items && items.length"
      :is="view"
      :items="filteredItems"
      :active-item="activeItem"
      @dir-item-clicked="onDirectoryItemClicked"
      @dir-item-dblclicked="onDirectoryItemDblClicked"
      @dir-item-starred="onDirectoryItemStarred"
      @dropdown-menu-item-clicked="onDropDownMenuItemClicked"
      @on-dir-context-menu="onContextMenu"
    />

    <div v-else-if="!isLoading">
      <slot name="noDataMessage">
        <p>Nothing here to see.</p>
      </slot>
    </div>

    <div class="p-4 text-right">
      <span class="block text-md font-normal text-white/60">
        <slot name="pagingMetaData">
          <template>
            {{ subheader }}
          </template>
        </slot>
      </span>

      <!-- Scroll trigger to load more productions -->
      <div
        v-if="infinite"
        ref="loadTrigger"
        class="load-trigger flex min-h-[8rem] w-full items-center justify-center"
      >
        <div v-if="isLoading" class="loading loading--inline m-0 inline-flex scale-125 items-center justify-center">
          <i class="fas fa-spinner fa-spin text-[1.5rem]"></i>
        </div>
      </div>

      <!-- "Show More" button - shown when infinite scrolling is disabled -->
      <button
        v-else-if="items && items.length && visibleCount < totalCount"
        class="border-0 bg-transparent px-3 py-1.5 text-sm text-white transition hover:text-producer-primary"
        @click="onViewMoreClicked"
      >
        Show More
        <span class="fas fa-chevron-down ml-1" />
      </button>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';

import ConduitEventsService from '../../services/conduitEventsService';

import DirectoryGrid from './DirectoryGrid.vue';
import DirectoryTable from './DirectoryTable.vue';
import DirectorySwitcher from './DirectorySwitcher.vue';

export default {
  name: 'Directory',

  components: {
    DirectoryGrid,
    DirectoryTable,
    DirectorySwitcher,
  },

  props: {
    header: {
      type: String,
      default: '',
    },

    subheader: {
      type: String,
      default: '',
    },

    showSubheader: {
      type: Boolean,
      default: false,
    },

    items: {
      type: Array,
      default: () => [],
    },

    totalCount: {
      type: Number,
    },

    pageSize: {
      type: Number,
    },

    activeItem: {
      type: [String, Number],
    },

    selectedComponentView: {
      type: String,
      default: 'tgb-directory-table',
    },

    infinite: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      totalVisibleCount: 0,
      closedView: true,
    };
  },

  methods: {
    loadMore() {
      if (!this.isLoading && this.visibleCount < this.totalCount) {
        this.$emit('load-more');
      }
    },

    onDirectoryItemStarred(item) {
      this.$emit('dir-item-starred', item);
    },

    onDirectoryItemClicked(item) {
      this.$emit('dir-item-clicked', item);

      ConduitEventsService.track(
          'producer.selected.production',
          {
            production_id: item._id,
            title: item.title,
            starred: item.flagged,
            tags: item.tags,
          },
          {
            ownerId: item.owner,
            userId: this.$store.state.user.id,
            isStaff: this.$store.getters.isStaff,
          },
      );
    },

    onDirectoryItemDblClicked(item) {
      if (item.deleted) { return; }

      this.$router.push({ name: 'production', params: { production_id: item._id } });

      ConduitEventsService.track(
          'producer.launched.production',
          {
            production_id: item._id,
            title: item.title,
            tags: item.tags,
            starred: item.flagged,
            trigger: 'dblclick',
            method: 'onDirectoryItemDblClicked',
          },
          {
            ownerId: item.owner,
            userId: this.$store.state.user.id,
            isStaff: this.$store.getters.isStaff,
          },
      );
    },

    onContextMenu(payload) {
      this.$emit('on-dir-context-menu', payload);
    },

    onDropDownMenuItemClicked(payload) {
      this.$emit('dropdown-menu-item-clicked', payload);
    },

    onViewMoreClicked() {
      this.closedView = false;

      if (this.pageSize) {
        // Add another page on the end
        this.visibleCount += this.pageSize;
      } else {
        // Just expand to show everything
        this.visibleCount = this.items.length;
      }

      if (this.items.length < this.totalCount) {
        // NOTE: If we preload the next page everytime they click this button,
        //       we can remove the loader on the screen
        this.$emit('load-more');
      }
    },
  },

  computed: {
    ...mapState({
      isLoading: (state) => state.production.loading,
    }),

    /**
    * Return paginated list of items using visibleCount
    */
    filteredItems() {
      return this.items.slice(0, this.visibleCount);
    },

    visibleCount: {
      get() {
        return this.totalVisibleCount;
      },

      set(count) {
        this.totalVisibleCount = count;
        this.$emit('update:total-visible-count', count);
      },
    },

    view: {
      get() {
        return {
          'tgb-directory-table': 'directory-table',
          'tgb-directory-grid': 'directory-grid',
        }[this.selectedComponentView];
      },

      set(view) {
        this.$emit('update:selected-component-view', view);
      },
    },
  },

  watch: {
    items(newItems, oldItems) {
      if (this.visibleCount === oldItems?.length) {
        // Change to the new length
        this.visibleCount = this.items.length;
      }
    },
  },

  mounted() {
    this.visibleCount = this.pageSize;

    // Gotta grab the dashboard wrapper since that's scrollable
    const containerEl = document.querySelector('.producer-dashboard-body__list');

    if (containerEl && this.infinite) {
      this.intersectionObserver = new IntersectionObserver(this.loadMore, { root: containerEl });
      this.intersectionObserver.observe(this.$refs.loadTrigger);
    }
  },
};
</script>
